import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation } from 'react-query';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowLeft,
  faArrowRight,
  faRocket,
  faSpinner,
  faFolder,
  faChevronRight,
  faChevronDown,
  faDatabase,
  faTable,
  faChartBar,
  faCircleCheck,
  faPlug,
  faMagnifyingGlass,
} from '@fortawesome/free-solid-svg-icons';
import toast from 'react-hot-toast';
import { useAppStore } from '../store';
import { domoApi, pbiApi, datasourcesApi, migrationsApi, assessmentApi } from '../services/api';
import { Card, Button, Select, PageHeader, Input, Badge, Spinner } from '../components/ui';

/*
|--------------------------------------------------------------------------
| Migration Page
|--------------------------------------------------------------------------
| A 4-step wizard:
|   1. Choose App / Page  - same tree picker UI used by the Assess page's
|      "Start Assessment" scope modal and the Project detail page's "Add
|      Apps / Reports" modal (GET /domo/apps-pages), but single-select via
|      radio buttons instead of checkboxes, since a migration targets one
|      report at a time.
|   2. Cards - lists every card on the chosen page (or every card across
|      the chosen app's pages), including its card type and Domo dataset.
|   3. Datasources - maps each Domo dataset to an existing Power BI
|      datasource.
|   4. Power BI Target - workspace + names, same as before, then Run.
|--------------------------------------------------------------------------
*/

const STEPS = ['Choose App / Page', 'Cards', 'Datasources', 'Power BI Target'];

export default function MigrationPage() {
  const navigate = useNavigate();

  const [step, setStep] = useState(0);
  const { migrationConfig, setMigrationConfig, setActiveJobId, resetProgress } = useAppStore();

  const cfg = migrationConfig;
  const set = (k, v) => setMigrationConfig({ [k]: v });

  /*
  |--------------------------------------------------------------------------
  | Step 1: App / Page picker (single-select, radio buttons)
  |--------------------------------------------------------------------------
  */

  const [scopeLoading, setScopeLoading] = useState(true);
  const [scopeError, setScopeError] = useState(null);
  const [apps, setApps] = useState([]);
  const [pages, setPages] = useState([]);
  const [scopeSearch, setScopeSearch] = useState('');
  const [expandedKeys, setExpandedKeys] = useState([]);

  // Single selection across both Apps and Pages: { type: 'app'|'page', id, item }
  const [selectedScope, setSelectedScope] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setScopeLoading(true);
      setScopeError(null);
      try {
        const resp = await assessmentApi.appsPages();
        const payload = resp.data?.data || resp.data || {};
        if (cancelled) return;
        setApps(Array.isArray(payload.apps) ? payload.apps : []);
        setPages(Array.isArray(payload.pages) ? payload.pages : []);
      } catch (err) {
        if (!cancelled) setScopeError(err.response?.data?.error || err.message || 'Failed to load apps and pages');
      } finally {
        if (!cancelled) setScopeLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const itemId = item => item?.id ?? item?.appId ?? item?.pageId ?? item?.dataAppId ?? item?.value;
  const itemName = item => item?.name ?? item?.title ?? item?.label ?? item?.pageName ?? item?.appName ?? String(itemId(item));
  const itemChildren = item => item?.children || item?.pages || item?.items || item?.cards || null;

  const toggleExpanded = key => {
    setExpandedKeys(prev => prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]);
  };

  const matchesSearch = item => itemName(item).toLowerCase().includes(scopeSearch.trim().toLowerCase());
  const filteredApps = apps.filter(matchesSearch);
  const filteredPages = pages.filter(matchesSearch);

  const selectScope = (type, item) => {
    const id = itemId(item);
    if (id == null) return;
    setSelectedScope({ type, id, item });
  };

  const renderScopeTree = (list, type, groupKey) => (
    <div style={{ display: 'flex', flexDirection: 'column' }}>
      {list.map((item, index) => {
        const id = itemId(item);
        const key = `${groupKey}-${id ?? index}`;
        const children = itemChildren(item);
        const hasChildren = Array.isArray(children) && children.length > 0;
        const expanded = expandedKeys.includes(key);
        const checked = selectedScope?.type === type && selectedScope?.id === id;

        return (
          <div key={key}>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                padding: '6px 4px',
                fontSize: '13px',
                borderRadius: 'var(--radius-sm)',
                background: checked ? 'var(--accent-light)' : 'transparent',
                cursor: 'pointer'
              }}
              onClick={() => selectScope(type, item)}
            >
              <button
                type="button"
                onClick={e => { e.stopPropagation(); if (hasChildren) toggleExpanded(key); }}
                aria-label={hasChildren ? (expanded ? 'Collapse' : 'Expand') : undefined}
                style={{
                  border: 'none',
                  background: 'transparent',
                  cursor: hasChildren ? 'pointer' : 'default',
                  padding: 0,
                  width: '14px',
                  display: 'flex',
                  alignItems: 'center',
                  color: 'var(--text-tertiary)',
                  fontSize: '10px'
                }}
              >
                {hasChildren ? <FontAwesomeIcon icon={expanded ? faChevronDown : faChevronRight} /> : null}
              </button>

              <FontAwesomeIcon icon={faFolder} style={{ color: '#f0b429', fontSize: '13px' }} />

              <span
                style={{
                  flex: '1 1 auto',
                  color: 'var(--accent)',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap'
                }}
              >
                {itemName(item)}
              </span>

              <input
                type="radio"
                name="migration-scope"
                checked={checked}
                onChange={() => selectScope(type, item)}
                onClick={e => e.stopPropagation()}
                style={{ cursor: 'pointer', flexShrink: 0 }}
              />
            </div>

            {hasChildren && expanded && (
              <div style={{ paddingLeft: '22px' }}>
                {renderScopeTree(children, type, `${key}-child`)}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );

  /*
  |--------------------------------------------------------------------------
  | Step 2: Cards + Domo datasets
  |--------------------------------------------------------------------------
  | Resolves the page ids to pull cards for: the selected page itself, or
  | every child page under the selected app (falls back to just the app
  | id if it has no nested pages).
  |--------------------------------------------------------------------------
  */

  const [cardsLoading, setCardsLoading] = useState(false);
  const [cards, setCards] = useState([]);
  const [datasets, setDatasets] = useState([]);
  const [domoDatasources, setDomoDatasources] = useState([]);
  const [cardDatasourceMap, setCardDatasourceMap] = useState({});

  const pageIdsForScope = useMemo(() => {
    if (!selectedScope) return [];
    if (selectedScope.type === 'page') return [selectedScope.id];

    const children = itemChildren(selectedScope.item);
    if (Array.isArray(children) && children.length) {
      return children.map(itemId).filter(id => id != null);
    }
    return [selectedScope.id];
  }, [selectedScope]);

  useEffect(() => {
    if (step !== 1 || !selectedScope) return;

    let cancelled = false;
    (async () => {
      setCardsLoading(true);
      try {
        const [cardResults, datasetResp] = await Promise.all([
          Promise.all(pageIdsForScope.map(pid =>
            domoApi.cards(pid).catch(() => ({ data: { data: [] } }))
          )),
          // domoApi.datasets().catch(() => ({ data: { data: [] } }))
        ]);

        if (cancelled) return;

        const allCards = cardResults.flatMap(resp => resp?.data?.data || resp?.data || []);
        setCards(allCards);
        setDatasets(datasetResp?.data?.data || datasetResp?.data || []);

        const datasetIds = [...new Map(allCards
          .map(card => card?.datasetId || card?.dataSetId || card?.detail?.datasourceId || card?.detail?.dataSetId)
          .filter(id => id != null)
          .map(id => [String(id), id])).values()];
        const datasourceResp = await datasourcesApi.list(datasetIds);
        if (cancelled) return;
        const datasourcePayload = datasourceResp?.data?.data || datasourceResp?.data || {};
        setDomoDatasources(datasourcePayload.domo || datasourcePayload.domoDatasources || []);
        setDatasources(datasourcePayload.powerbi || datasourcePayload.powerBi || datasourcePayload.powerBIDatasources || []);
      } catch (err) {
        if (!cancelled) toast.error('Failed to load cards and datasources for the selected scope');
      } finally {
        if (!cancelled) setCardsLoading(false);
      }
    })();

    return () => { cancelled = true; };
  }, [step, selectedScope, pageIdsForScope]);

  const datasetNameFor = card => {
    const dsId = cardDatasetId(card);
    if (!dsId) return '-';
    const ds = datasets.find(d => String(d.id || d.datasetId) === String(dsId));
    return ds?.name || ds?.datasetName || dsId;
  };

  const cardDatasetId = card =>
    card?.datasetId || card?.dataSetId || card?.detail?.datasourceId || card?.detail?.dataSetId;

  const datasourceRecordId = datasource =>
    datasource?.id || datasource?.datasetId || datasource?.datasourceId;

  const cardTypeFor = card => card?.chartType || card?.cardType || card?.type || '-';

  const setDatasetDatasource = (datasetId, datasourceId) => {
    setCardDatasourceMap(prev => ({ ...prev, [datasetId]: datasourceId }));
  };

  const datasourceLabel = ds =>
    `${ds?.datasourceType || 'Datasource'} - ${ds?.connectionDetails?.url || ds?.connectionDetails?.connectionString || ds?.connectionDetails?.path || ds?.connectionDetails?.server || ds?.datasourceId || ''}`;

  /*
  |--------------------------------------------------------------------------
  | Power BI Connection (workspaces)
  |--------------------------------------------------------------------------
  | Connects when Step 4 (Power BI Target) is reached so the workspace
  | picker loads from the workspace API when that tab is opened.
  |--------------------------------------------------------------------------
  */

  const [workspaces, setWorkspaces] = useState([]);
  const [datasources, setDatasources] = useState([]);
  const [pbiConnected, setPbiConnected] = useState(false);
  const [pbiConnecting, setPbiConnecting] = useState(false);

  useEffect(() => {
    if (step !== 3 || pbiConnected) return;

    let cancelled = false;

    (async () => {
      setPbiConnecting(true);
      try {
        const res = await pbiApi.connect();
        const token = res?.data?.token;
        if (!token) throw new Error('No token');

        const ws = await pbiApi.workspaces({ token });
        if (cancelled) return;

        const workspaceData = ws?.data?.data || ws?.data || ws || [];
        setWorkspaces(Array.isArray(workspaceData) ? workspaceData : []);
        setPbiConnected(true);

      } catch (err) {
        if (!cancelled) toast.error('Power BI connection failed');
      } finally {
        if (!cancelled) setPbiConnecting(false);
      }
    })();

    return () => { cancelled = true; };
  }, [step, pbiConnected]);

  /*
  |--------------------------------------------------------------------------
  | Run Migration
  |--------------------------------------------------------------------------
  */

  const runMutation = useMutation((body) => migrationsApi.create(body), {
    onSuccess: (data) => {
      resetProgress();
      setActiveJobId(data.data.id);
      toast.success('Migration started!');
      navigate('/execution');
    },
    onError: () => toast.error('Failed to start migration'),
  });

  const handleRun = () => {
    const selectedDatasourceDetails = cards
      .map((card, index) => {
        const cardId = card.id || card.cardId || card.urn || index;
        const datasetId = cardDatasetId(card);
        const datasourceId = cardDatasourceMap[datasetId];
        if (!datasourceId) return null;

        const domoDatasource = domoDatasources.find(datasource =>
          String(datasourceRecordId(datasource)) === String(datasetId)
        );
        const powerBiDatasource = datasources.find(datasource =>
          String(datasourceRecordId(datasource)) === String(datasourceId)
        );

        return {
          cardId,
          datasetId,
          datasourceId,
          datasource: powerBiDatasource || null,
          domoDatasource: domoDatasource || null,
        };
      })
      .filter(Boolean);

    runMutation.mutate({
      scopeType: selectedScope?.type,
      pageId: selectedScope?.type === 'page' ? selectedScope.id : cfg.pageId,
      pageName: selectedScope ? itemName(selectedScope.item) : cfg.pageName,
      appId: selectedScope?.type === 'app' ? selectedScope.id : undefined,
      cardMappings: cards
        .map((card, index) => {
          const cardId = card.id || card.cardId || card.urn || index;
          const datasetId = cardDatasetId(card);
          const datasourceId = cardDatasourceMap[datasetId];
          return datasourceId ? { cardId, datasetId, datasourceId } : null;
        })
        .filter(Boolean),
      datasourceDetails: selectedDatasourceDetails,
      workspaceId: cfg.workspaceId,
      reportName: cfg.reportName,
      datasetName: cfg.datasetName,
      autoConvertDAX: cfg.autoConvertDAX,
    });
  };

  const canGoToStep2 = !!selectedScope;
  const canGoToStep3 = true; // cards mapping is optional per card

  /* ================= UI ================= */
  return (
    <div className="animate-fade-in" style={{ padding: 24 }}>
      <PageHeader
        title="New Migration"
        subtitle="Domo → Power BI"
      />

      {/* STEP BAR */}
      <div style={{ display: 'flex', marginBottom: 22, gap: 8 }}>
        {STEPS.map((s, i) => {
          const active = i === step;
          const done = i < step;
          return (
            <div
              key={i}
              onClick={() => i <= step && setStep(i)}
              style={{
                flex: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 8,
                padding: '10px 12px',
                borderRadius: 'var(--radius-md)',
                cursor: i <= step ? 'pointer' : 'default',
                background: active ? 'var(--accent)' : done ? 'var(--accent-light)' : 'var(--bg-secondary)',
                color: active ? '#fff' : done ? 'var(--accent)' : 'var(--text-secondary)',
                fontSize: 13,
                fontWeight: active ? 600 : 500,
                transition: 'background .15s, color .15s'
              }}
            >
              {done ? <FontAwesomeIcon icon={faCircleCheck} /> : <span>{i + 1}.</span>}
              {s}
            </div>
          );
        })}
      </div>

      {/* ================= STEP 0: Choose App / Page ================= */}
      {step === 0 && (
        <Card style={{ padding: 0, overflow: 'hidden' }}>
          <div style={{ padding: '16px 20px', borderBottom: '0.5px solid var(--border)' }}>
            <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 4 }}>Choose an App or Page</div>
            <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
              Pick one app or one page to migrate. Only a single selection is allowed.
            </div>
          </div>

          <div style={{ padding: '14px 20px', borderBottom: '0.5px solid var(--border)' }}>
            <div style={{ position: 'relative' }}>
              <FontAwesomeIcon
                icon={faMagnifyingGlass}
                style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-tertiary)', fontSize: 12 }}
              />
              <input
                type="text"
                value={scopeSearch}
                onChange={e => setScopeSearch(e.target.value)}
                placeholder="Filter apps and pages..."
                style={{
                  width: '100%',
                  height: 36,
                  border: '0.5px solid var(--border-med)',
                  borderRadius: 'var(--radius-md)',
                  padding: '0 12px 0 32px',
                  outline: 'none',
                  background: 'var(--bg-secondary)',
                  color: 'var(--text-primary)',
                  fontSize: 13
                }}
              />
            </div>
          </div>

          <div style={{ padding: 20, minHeight: 200 }}>
            {scopeLoading ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, justifyContent: 'center', padding: '30px 0', color: 'var(--text-secondary)', fontSize: 13 }}>
                <FontAwesomeIcon icon={faSpinner} spin /> Loading apps and pages...
              </div>
            ) : scopeError ? (
              <div style={{ color: 'var(--danger)', fontSize: 13, textAlign: 'center', padding: '20px 0' }}>{scopeError}</div>
            ) : (
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 32 }}>
                <div>
                  <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-secondary)', textTransform: 'uppercase', marginBottom: 8 }}>
                    Apps ({filteredApps.length})
                  </div>
                  {!filteredApps.length ? (
                    <div style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>No apps found.</div>
                  ) : renderScopeTree(filteredApps, 'app', 'app')}
                </div>
                <div>
                  <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-secondary)', textTransform: 'uppercase', marginBottom: 8 }}>
                    Pages ({filteredPages.length})
                  </div>
                  {!filteredPages.length ? (
                    <div style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>No pages found.</div>
                  ) : renderScopeTree(filteredPages, 'page', 'page')}
                </div>
              </div>
            )}
          </div>

          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '14px 20px', borderTop: '0.5px solid var(--border)' }}>
            <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
              {selectedScope ? `Selected: ${itemName(selectedScope.item)}` : 'Nothing selected yet'}
            </span>
            <Button variant="primary" disabled={!canGoToStep2} onClick={() => setStep(1)}>
              Next <FontAwesomeIcon icon={faArrowRight} />
            </Button>
          </div>
        </Card>
      )}

      {/* ================= STEP 1: Cards ================= */}
      {step === 1 && (
        <Card style={{ padding: 0, overflow: 'hidden' }}>
          <div style={{ padding: '16px 20px', borderBottom: '0.5px solid var(--border)' }}>
            <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 4 }}>
              Cards in {selectedScope ? itemName(selectedScope.item) : 'selected scope'}
            </div>
            <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
              Review the card type and source Domo dataset before configuring datasource mappings.
            </div>
          </div>

          <div style={{ padding: 20 }}>
            {cardsLoading ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, justifyContent: 'center', padding: '30px 0', color: 'var(--text-secondary)', fontSize: 13 }}>
                <FontAwesomeIcon icon={faSpinner} spin /> Loading cards...
              </div>
            ) : !cards.length ? (
              <div style={{ textAlign: 'center', padding: '30px 0', color: 'var(--text-tertiary)', fontSize: 13 }}>
                No cards found for this selection.
              </div>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                  <thead>
                    <tr>
                      {['Card', 'Card Type', 'Domo Dataset'].map(h => (
                        <th key={h} style={{
                          textAlign: 'left', padding: '8px 12px',
                          fontSize: 11, fontWeight: 600, color: 'var(--text-secondary)',
                          borderBottom: '0.5px solid var(--border)',
                          textTransform: 'uppercase', letterSpacing: '.04em'
                        }}>
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {cards.map((card, index) => {
                      const cardId = card.id || card.cardId || card.urn || index;
                      return (
                        <tr key={cardId}>
                          <td style={{ padding: '10px 12px', borderBottom: '0.5px solid var(--border)' }}>
                            <FontAwesomeIcon icon={faChartBar} style={{ color: 'var(--accent)', marginRight: 8 }} />
                            {card.name || card.title || `Card ${index + 1}`}
                          </td>
                          <td style={{ padding: '10px 12px', borderBottom: '0.5px solid var(--border)' }}>
                            {cardTypeFor(card)}
                          </td>
                          <td style={{ padding: '10px 12px', borderBottom: '0.5px solid var(--border)' }}>
                            <FontAwesomeIcon icon={faDatabase} style={{ color: 'var(--text-tertiary)', marginRight: 8 }} />
                            {datasetNameFor(card)}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '14px 20px', borderTop: '0.5px solid var(--border)' }}>
            <Button variant="secondary" onClick={() => setStep(0)}>
              <FontAwesomeIcon icon={faArrowLeft} /> Back
            </Button>
            <Button variant="primary" disabled={!canGoToStep3} onClick={() => setStep(2)}>
              Next <FontAwesomeIcon icon={faArrowRight} />
            </Button>
          </div>
        </Card>
      )}

      {/* ================= STEP 2: Datasources ================= */}
      {step === 2 && (
        <Card style={{ padding: 0, overflow: 'hidden' }}>
          <div style={{ padding: '16px 20px', borderBottom: '0.5px solid var(--border)' }}>
            <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 4 }}>Datasource Mapping</div>
            <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
              Review each Domo datasource and map it to one of the available Power BI datasources.
            </div>
          </div>

          <div style={{ padding: 20 }}>
            {!domoDatasources.length ? (
              <div style={{ textAlign: 'center', padding: '30px 0', color: 'var(--text-tertiary)', fontSize: 13 }}>
                No Domo datasources found for this selection.
              </div>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                  <thead>
                    <tr>
                      {['Domo Datasource', 'Power BI Datasource'].map(h => (
                        <th key={h} style={{
                          textAlign: 'left', padding: '8px 12px',
                          fontSize: 11, fontWeight: 600, color: 'var(--text-secondary)',
                          borderBottom: '0.5px solid var(--border)',
                          textTransform: 'uppercase', letterSpacing: '.04em'
                        }}>
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {domoDatasources.map((dataset, index) => {
                      const datasetId = dataset.id || dataset.datasetId || index;
                      return (
                        <tr key={datasetId}>
                          <td style={{ padding: '10px 12px', borderBottom: '0.5px solid var(--border)' }}>
                            <FontAwesomeIcon icon={faDatabase} style={{ color: 'var(--text-tertiary)', marginRight: 8 }} />
                            {dataset?.datasource?.host || dataset.datasetName || datasetId}
                          </td>
                          <td style={{ padding: '10px 12px', borderBottom: '0.5px solid var(--border)' }}>
                            <select
                              value={cardDatasourceMap[datasetId] || ''}
                              onChange={e => setDatasetDatasource(datasetId, e.target.value)}
                              style={{
                                width: '100%',
                                padding: '6px 8px',
                                border: '0.5px solid var(--border-med)',
                                borderRadius: 'var(--radius-sm)',
                                background: 'var(--bg-secondary)',
                                color: 'var(--text-primary)',
                                fontSize: 12
                              }}
                            >
                              <option value="">Unmapped</option>
                              {datasources.map(ds => (
                                <option key={ds.datasourceId} value={ds.datasourceId}>
                                  {datasourceLabel(ds)}
                                </option>
                              ))}
                            </select>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '14px 20px', borderTop: '0.5px solid var(--border)' }}>
            <Button variant="secondary" onClick={() => setStep(1)}>
              <FontAwesomeIcon icon={faArrowLeft} /> Back
            </Button>
            <Button variant="primary" onClick={() => setStep(3)}>
              Next <FontAwesomeIcon icon={faArrowRight} />
            </Button>
          </div>
        </Card>
      )}

      {/* ================= STEP 3: Power BI Target ================= */}
      {step === 3 && (
        <Card>
          <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 4, display: 'flex', alignItems: 'center', gap: 8 }}>
            <FontAwesomeIcon icon={faPlug} style={{ color: 'var(--accent)' }} />
            Power BI Target
          </div>
          <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 16 }}>
            Choose the destination workspace and name the resulting dataset/report.
          </div>

          {pbiConnecting ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: 'var(--text-secondary)', fontSize: 13, padding: '20px 0' }}>
              <Spinner size={18} /> Connecting to Power BI...
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              <Select
                label="Workspace"
                value={cfg.workspaceId}
                onChange={e => set('workspaceId', e.target.value)}
              >
                <option value="">Select workspace</option>
                {workspaces.map(w => (
                  <option key={w.id} value={w.id}>{w.name}</option>
                ))}
              </Select>

              <Input
                label="Dataset name"
                value={cfg.datasetName}
                onChange={e => set('datasetName', e.target.value)}
              />

              <Input
                label="Report name"
                value={cfg.reportName}
                onChange={e => set('reportName', e.target.value)}
              />

              <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
                <Badge variant="accent">{cards.length}</Badge> card{cards.length === 1 ? '' : 's'},{' '}
                <Badge variant="accent">{Object.keys(cardDatasourceMap).length}</Badge> mapped to a Power BI datasource
              </div>

              <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 6 }}>
                <Button variant="secondary" onClick={() => setStep(2)}>
                  <FontAwesomeIcon icon={faArrowLeft} /> Back
                </Button>
                <Button variant="primary" onClick={handleRun} loading={runMutation.isLoading}>
                  <FontAwesomeIcon icon={faRocket} /> Start Migration
                </Button>
              </div>
            </div>
          )}
        </Card>
      )}
    </div>
  );
}
